/**
 * Staged-file preview — a small "did I attach the right file?" grid shown in
 * the import pickers while the file is still just staged. Until now the only
 * evidence was the file's name and type; a wrong spreadsheet (last week's
 * order guide instead of this week's price list) was discovered only after
 * the import ran.
 *
 * What is previewed: the first ~5 rows × ~5 columns of CSV/TSV/text files
 * (read from a 32 KB head slice, never the whole file) and of Excel files
 * (SheetJS with `sheetRows` capped, first non-empty sheet). PDFs and images
 * return null — a name is all an import picker can honestly show for those.
 *
 * Pure core (canPreviewName / gridFromText) is mirrored for the plain
 * <script> pages in public/js/file-preview.js and pinned by
 * public/js/file-preview.test.mjs — change one, change the other. The async
 * previewFile glue is browser-only (File.slice, XLSX global) and untested in
 * node by design.
 */
export const PREVIEW_ROWS = 5;
export const PREVIEW_COLS = 5;
export const PREVIEW_SLICE_BYTES = 32768;

const PREVIEWABLE_TEXT = /\.(csv|tsv|txt)$/i;
const PREVIEWABLE_EXCEL = /\.(xlsx|xls)$/i;

export function canPreviewName(name) {
  const n = String(name || '');
  return PREVIEWABLE_TEXT.test(n) || PREVIEWABLE_EXCEL.test(n);
}

/* ── Delimited-text core (pure) ──────────────────────────────────────────── */

// One pass, quote-aware: a quoted comma/tab is data, and a quoted newline is
// part of the cell rather than a row break (vendor sheets love both).
function parseDelimitedRows(text, delimiter) {
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cell += '"'; i += 1; } else { inQuotes = false; }
      } else {
        cell += ch;
      }
      continue;
    }
    if (ch === '"' && cell === '') { inQuotes = true; continue; }
    if (ch === delimiter) { row.push(cell); cell = ''; continue; }
    if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      continue;
    }
    cell += ch;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

// Tab wins ties: a comma is common prose punctuation in a vendor sheet, a tab
// almost never is.
function detectDelimiter(text) {
  const line = (text.split(/\r?\n/).find((l) => l.trim() !== '')) || '';
  const tabs = (line.match(/\t/g) || []).length;
  const commas = (line.match(/,/g) || []).length;
  return tabs > 0 && tabs >= commas ? '\t' : ',';
}

/**
 * Text → clipped grid. `options.truncatedInput` marks a head slice of a larger
 * file: the final (possibly half-cut) line is dropped unless the text ends on
 * a newline, and `moreRows` is reported whenever more could exist. Whitespace
 * is trimmed per cell; everything else is verbatim.
 */
export function gridFromText(text, options) {
  const opts = options || {};
  const maxRows = opts.maxRows || PREVIEW_ROWS;
  const maxCols = opts.maxCols || PREVIEW_COLS;
  let source = String(text || '');
  let truncatedInput = opts.truncatedInput === true;
  if (truncatedInput && /[\r\n]$/.test(source)) truncatedInput = false;
  if (truncatedInput) {
    const lastBreak = Math.max(source.lastIndexOf('\n'), source.lastIndexOf('\r'));
    source = lastBreak >= 0 ? source.slice(0, lastBreak) : '';
  }
  const parsed = parseDelimitedRows(source, detectDelimiter(source));
  const moreRows = truncatedInput || parsed.length > maxRows;
  const kept = parsed.slice(0, maxRows);
  const moreCols = kept.some((row) => row.length > maxCols);
  return {
    rows: kept.map((row) => row.slice(0, maxCols).map((cell) => cell.trim())),
    moreRows,
    moreCols,
  };
}

/* ── Browser glue (untested in node, by design) ──────────────────────────── */

const previewCache = new Map(); // dedupe signature → Promise<grid|null>

function excelGrid(file, maxRows, maxCols) {
  const XLSX = globalThis.XLSX;
  if (!XLSX) return Promise.resolve(null);
  return file.arrayBuffer().then((buffer) => {
    const workbook = XLSX.read(buffer, { type: 'array', sheetRows: maxRows + 1 });
    // First sheet with any rows — the import's sheet choice is server-side,
    // so the preview's job is recognition, not fidelity.
    let rows = [];
    for (const name of workbook.SheetNames) {
      const sheet = workbook.Sheets[name];
      rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: '' });
      if (rows.length) break;
    }
    const kept = rows.slice(0, maxRows);
    return {
      rows: kept.map((row) => row.slice(0, maxCols).map((cell) => String(cell == null ? '' : cell).trim())),
      moreRows: rows.length > maxRows,
      moreCols: kept.some((row) => row.length > maxCols),
    };
  });
}

/**
 * file → grid, or null when the file kind has no honest preview. Cached per
 * file identity (the fileDedupe signature) so re-rendering the staged list
 * never re-reads bytes. Never rejects — a preview must not break a picker.
 */
export function previewFile(file, options) {
  const opts = options || {};
  const maxRows = opts.maxRows || PREVIEW_ROWS;
  const maxCols = opts.maxCols || PREVIEW_COLS;
  const key = file
    ? `${file.name || ''}|${file.size == null ? 0 : file.size}|${file.lastModified || 0}`
    : '';
  if (previewCache.has(key)) return previewCache.get(key);
  let promise;
  if (!file || !canPreviewName(file.name)) {
    promise = Promise.resolve(null);
  } else if (PREVIEWABLE_EXCEL.test(String(file.name || ''))) {
    promise = excelGrid(file, maxRows, maxCols).catch(() => null);
  } else {
    promise = file.slice(0, PREVIEW_SLICE_BYTES).text()
      .then((text) => gridFromText(text, {
        maxRows,
        maxCols,
        truncatedInput: file.size > PREVIEW_SLICE_BYTES,
      }))
      .catch(() => null);
  }
  previewCache.set(key, promise);
  return promise;
}

/** Markup for the clipped grid. Escaped; cells ellipsize via the caller's CSS. */
export function gridToHtml(grid) {
  if (!grid || !grid.rows || !grid.rows.length) return '';
  const esc = (v) => String(v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  const body = grid.rows.map((row) =>
    `<tr>${row.map((cell) => `<td>${esc(cell)}</td>`).join('')}${grid.moreCols ? '<td class="fp-more">…</td>' : ''}</tr>`
  ).join('');
  const tail = grid.moreRows
    ? `<tr><td class="fp-more" colspan="${grid.rows[0].length + (grid.moreCols ? 1 : 0)}">…</td></tr>`
    : '';
  return `<table class="file-preview-grid"><tbody>${body}${tail}</tbody></table>`;
}
